import { IPromise } from "angular";
import * as _ from "lodash";

import * as Models from "../Models";
import { Name } from "../Models/ContractAmendment/AmendNameAddressRequest";
import * as Routes from "../Routes";
import * as Services from "../Services";
import * as Shared from "../Shared";

export class MainCtrl {
  private showLogging: boolean = false;
  private roleList: Models.Auth.RoleList.RoleList = new Models.Auth.RoleList.RoleList();

  public userPermissions: Models.Auth.UserPermission.UserPermissions = new Models.Auth.UserPermission.UserPermissions();
  public user: Models.UserModel.UserModel;
  public userWorkIssues: Array<Models.Issues.IBaseIssue>;
  public numberOfIssueUpdates: number;

  public static $inject = [
    "$scope",
    "$state",
    "$timeout",
    "UserService",
    "$rootScope",
    "StatePermissionsMappingService",
    "$uibModal",
    "WorkIssueApiClient",
    "igs.messageBox",
  ];

  constructor(
    public $scope: any, //This must remain as "any" because of the idle service stuff.
    public $state: angular.ui.IStateService,
    public $timeout: angular.ITimeoutService,
    private userService: Services.UserService.IUserService,
    private $rootScope: any,
    private statePermissionsMappingService: Services.StatePermissionsMappingService.IStatePermissionsMappingService,
    private $uibModal: angular.ui.bootstrap.IModalService,
    private workIssueApiClient: Services.WorkIssueApiClient.IWorkIssueApiClient,
    private messageBox: any,
  ) {
    $scope.mainCtrl = this;

    this.userService.getCurrentUser().then(
      (data: Models.UserModel.UserModel) => {
        this.user = data;

        if (!this.user) {
          this.$state.go("login", {
            statename: this.$state.current.name,
            returnStateParams: this.$state.params,
          });
        } else {
          this.setWatchToEnsureScreenAccessToScreens($scope);

          this.setPageDisplayAvailability();
          if (this.userPermissions.manageMyIssues) {
            this.updateNotificationBarInfo();
          }

          if (<any>window && (<any>window).FS) {
            (<any>window).FS.identify(this.user.userId, {
              displayName: this.user.displayName,
              email: this.user.emailAddress,
            });
          }
        }
      },
      () => {
        if (!this.user) {
          this.$state.go("login", {
            statename: this.$state.current.name,
            returnStateParams: this.$state.params,
          });
        }
      },
    );

    this.setWatchOnIssueUpsert($scope);
  }

  public updateNotificationBarInfo = (): void => {
    this.workIssueApiClient.getNotificationBarInfo(this.user.userId).then((response: any) => {
      this.userWorkIssues = response.issues;
      this.numberOfIssueUpdates = response.unseenAssignedIssuesCount;
    });
  };

  private goToIssues = (): void => {
    this.$state.go("main.allIssues", {
      performSearch: true,
      onlyMine: true,
    });
  };

  private setWatchOnIssueUpsert = ($scope: any): void => {
    $scope.$watch(
      () => {
        return this.$state.params.issueId;
      },
      (newVal: any, oldVal: any, scope: any) => {
        if (this.user && (newVal || oldVal)) {
          var issueId = newVal || oldVal;
          this.workIssueApiClient.markIssueAsSeen(issueId, this.user.userId).then((response: any) => {
            this.userWorkIssues = response.issues;
            this.numberOfIssueUpdates = response.unseenAssignedIssuesCount;
          });
        }
      },
    );
  };

  private setPageDisplayAvailability = (): void => {
    if (!!this.user) {
      this.setPagesUserCanSeeFromRoles(this.user.roles);
    } else {
      this.userService.getCurrentUser().then((data) => {
        this.user = data;

        if (!this.user) {
          this.$state.go("login", {
            statename: this.$state.current.name,
            returnStateParams: this.$state.params,
          });
        } else {
          this.setPagesUserCanSeeFromRoles(this.user.roles);
        }
      });
    }
  };

  private setPagesUserCanSeeFromRoles = (roles: Models.RoleModel.RoleModel[]): void => {
    let userRoles = roles.map((r) => {
      return r.name;
    });

    this.userPermissions.showEpcCalculator = _.includes(userRoles, this.roleList.useEpcCalculator);
    this.userPermissions.showResiPaymentCapture = _.includes(userRoles, this.roleList.captureResidentialPayment);
    this.userPermissions.showInvoiceDetails = _.includes(userRoles, this.roleList.manageResidentialInvoices);
    this.userPermissions.manageResiInvoice = _.includes(userRoles, this.roleList.manageResidentialInvoices);
    this.userPermissions.showCommunityPaymentCapture = _.includes(userRoles, this.roleList.captureCommunityPayment);
    this.userPermissions.showCreditCheck = _.includes(userRoles, this.roleList.performCreditCheck);
    this.userPermissions.showEpcAssumedValues = _.includes(userRoles, this.roleList.manageEpcAssumedValues);
    this.userPermissions.showUpdatePaymentMethod = _.includes(userRoles, this.roleList.captureCommunityPayment);
    this.userPermissions.showCreateCustomer = _.includes(userRoles, this.roleList.createCustomer);
    this.userPermissions.showSearchCustomers =
      _.includes(userRoles, this.roleList.searchCustomers) ||
      this.user.groups.find((x) => x.name.endsWith("Sales")) != null;
    this.userPermissions.showCustomerDetails = _.includes(userRoles, this.roleList.viewCustomerDetails);
    this.userPermissions.showEditCustomerAdminDetails = _.includes(userRoles, this.roleList.editCustomerAdminDetails);
    this.userPermissions.showCommercialCustomerCapture = _.includes(userRoles, this.roleList.captureCommercialCustomer);
    this.userPermissions.showDocumentManagement = _.includes(userRoles, this.roleList.manageCustomerDocuments);
    this.userPermissions.manageMyIssues = _.includes(userRoles, this.roleList.manageMyIssues);
    this.userPermissions.manageMyTeamsIssues = _.includes(userRoles, this.roleList.manageMyTeamsIssues);
    this.userPermissions.manageAllIssues = _.includes(userRoles, this.roleList.manageAllIssues);
    this.userPermissions.manageReferenceValues = _.includes(userRoles, this.roleList.manageReferenceValues);
    this.userPermissions.manageComponents = _.includes(userRoles, this.roleList.manageComponents);
    this.userPermissions.showCommercialBilling = _.includes(userRoles, this.roleList.manageCommercialInvoices);
    this.userPermissions.showMilestoneInvoiceQueue =
      _.includes(userRoles, this.roleList.approveEpcInvoices) ||
      _.includes(userRoles, this.roleList.submitEpcInvoices) ||
      _.includes(userRoles, this.roleList.approveBatches);
    this.userPermissions.approveBatches = _.includes(userRoles, this.roleList.approveBatches);
    this.userPermissions.approveEpcInvoices = _.includes(userRoles, this.roleList.approveEpcInvoices);
    this.userPermissions.financeApproveBatches = _.includes(userRoles, this.roleList.financeApproveBatches);
    this.userPermissions.manageGeneralBanners = _.includes(userRoles, this.roleList.manageGeneralBanners);
  };

  private setWatchToEnsureScreenAccessToScreens = ($scope: any): void => {
    $scope.$watch(
      () => {
        return this.$state.current;
      },
      (newValue: any) => {
        this.ensureAccessToScreen(newValue);
      },
    );
  };

  private ensureAccessToScreen = (state: angular.ui.IState): void => {
    const permissionNamesToCheck = this.statePermissionsMappingService.getRoleNameFromStateName(state.name);
    if (this.showLogging) {
      console.log(`ensureAccessToScreen => ${state.name} - ${permissionNamesToCheck}`);
    }
    if (permissionNamesToCheck[0] !== this.roleList.noRoleForState) {
      if (!!this.user) {
        this.ensureUserIsInCorrectRole(permissionNamesToCheck, state);
      } else {
        this.userService.getCurrentUser().then((data) => {
          this.user = data;

          if (!this.user) {
            this.$state.go("login", {
              statename: this.$state.current.name,
              returnStateParams: this.$state.params,
            });
          } else {
            this.ensureUserIsInCorrectRole(permissionNamesToCheck, state);
          }
        });
      }
    }
  };

  private ensureUserIsInCorrectRole = (permissionNamesToCheck: string[], state: angular.ui.IState): void => {
    const promises = new Array<IPromise<any>>();

    permissionNamesToCheck.forEach((permission) => {
      promises.push(this.userService.isUserInRole(permission));
    });

    Promise.all(promises).then(
      (results) => {
        if (results.every((x) => x === false)) {
          this.$state.go("main.customersearchv2");
        }
      },
      () => {
        this.$state.go("login", {
          returnStateParams: this.$state.params,
          statename: this.$state.current.name,
        });
      },
    );
  };

  public getPageBackgroundColor = (): any => {
    return { "background-color": "#FF0000" };
  };

  public viewIssueDetail = (issueId: number): void => {
    this.$state.go("main.upsertIssue", { issueId: issueId });
  };

  public returnToHomePage = (): void => {
    this.$state.go("main.customersearchv2");
  };

  public goToEpcSandbox = (): void => {
    this.$state.go("main.costCalculationSandbox");
  };
  public goToLogout = (): void => {
    let origin = window.location.origin.replace(":3000", ":5001");
    if (origin.indexOf("localhost") !== -1) origin = origin.replace("http://", "https://");

    window.location.href = origin + "/api/identity/logout";
  };
}
